// src/pages/layout/layout.js
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Layout, Menu, Button, Drawer, Avatar, Dropdown, Typography
} from 'antd';
import {
  HomeOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuOutlined,
  TeamOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TagsOutlined,
  UserSwitchOutlined,
  FileTextOutlined,
  DollarOutlined,
  BarChartOutlined,
  FileAddOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  SettingOutlined,
  FormOutlined,
  RobotOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { LoginContext } from '../../context/loginContext';

const { Header, Content } = Layout;
const { Text } = Typography;

const MainLayout = ({ children }) => {
  const { user, logout } = useContext(LoginContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    // Con HashRouter, usar navigate sin el hash
    window.location.href = '/#/login';
  };

  // Función para obtener items del menú según el rol
  const getMenuItems = () => {
    // Intentar obtener el rol de diferentes formas posibles
    const userRole = user?.tiu_descripcion || user?.tiu_descripcion?.toUpperCase() || '';
    const userTipoUsuario = user?.usu_tipousuario;
    
    
    // Si no hay tiu_descripcion pero hay usu_tipousuario, intentar mapear
    let roleToCheck = userRole.toUpperCase();
    if (!roleToCheck && userTipoUsuario) {
      // Mapeo basado en el número: 1 = ADMINISTRADOR, otros = ALUMNO
      if (userTipoUsuario === 1) {
        roleToCheck = 'ADMINISTRADOR';
      } else {
        roleToCheck = 'ALUMNO';
      }
      console.log('🔍 Rol inferido desde usu_tipousuario:', roleToCheck);
    }

    switch (roleToCheck) {
      case 'ADMINISTRADOR':
        return [
          {
            key: '/home-administrador',
            icon: <HomeOutlined />,
            label: <Link to="/home-administrador">Inicio</Link>,
          },
          {
            key: '/agenda-calendario',
            icon: <CalendarOutlined />,
            label: <Link to="/agenda-calendario">Agenda</Link>,
          },
          {
            key: '/dashboard',
            icon: <BarChartOutlined />,
            label: <Link to="/dashboard">Dashboard</Link>,
          },
          {
            key: 'alumnos-submenu',
            icon: <TeamOutlined />,
            label: 'Alumnos',
            children: [
              {
                key: '/administracion-alumnos',
                icon: <TeamOutlined />,
                label: <Link to="/administracion-alumnos">Alumnos</Link>,
              },
              {
                key: '/registro-pagos',
                icon: <DollarOutlined />,
                label: <Link to="/registro-pagos">Pagos</Link>,
              },
            ],
          },
          {
            key: 'configuracion-submenu',
            icon: <SettingOutlined />,
            label: 'Configuración',
            children: [
              {
                key: '/administracion-canchas',
                icon: <EnvironmentOutlined />,
                label: <Link to="/administracion-canchas">Canchas</Link>,
              },
              {
                key: '/administracion-categorias',
                icon: <TagsOutlined />,
                label: <Link to="/administracion-categorias">Categorías</Link>,
              },
              {
                key: '/administracion-planes',
                icon: <FileTextOutlined />,
                label: <Link to="/administracion-planes">Planes</Link>,
              },
              {
                key: '/administracion-profesores',
                icon: <UserSwitchOutlined />,
                label: <Link to="/administracion-profesores">Profesores</Link>,
              },
              {
                key: '/configuracion-bot',
                icon: <RobotOutlined />,
                label: <Link to="/configuracion-bot">Bot</Link>,
              },
              {
                key: '/asistencia-bot',
                icon: <CheckCircleOutlined />,
                label: <Link to="/asistencia-bot">Asistencia Bot</Link>,
              },
            ],
          },
          {
            key: 'solicitudes-submenu',
            icon: <FormOutlined />,
            label: 'Solicitudes',
            children: [
              {
                key: '/solicitudes-nuevos-alumnos',
                icon: <FileAddOutlined />,
                label: <Link to="/solicitudes-nuevos-alumnos">Solicitud Alumno</Link>,
              },
              {
                key: '/solicitudes-clase-alumno',
                icon: <CalendarOutlined />,
                label: <Link to="/solicitudes-clase-alumno">Solicitud Clase</Link>,
              },
            ],
          },
        ];
      case 'ALUMNO':
        return [
          {
            key: '/home-alumno',
            icon: <HomeOutlined />,
            label: <Link to="/home-alumno">Inicio</Link>,
          },
          {
            key: '/perfil',
            icon: <UserOutlined />,
            label: <Link to="/perfil">Mi Perfil</Link>,
          },
          {
            key: '/mis-creditos',
            icon: <CreditCardOutlined />,
            label: <Link to="/mis-creditos">Mis Créditos</Link>,
          },
          {
            key: '/mi-historial-pagos',
            icon: <DollarOutlined />,
            label: <Link to="/mi-historial-pagos">Mis compras</Link>,
          },
          {
            key: '/mis-clases',
            icon: <CalendarOutlined />,
            label: <Link to="/mis-clases">Mis Clases</Link>,
          },
          {
            key: '/solicitud-clase',
            icon: <CalendarOutlined />,
            label: <Link to="/solicitud-clase">Solicitud Clase</Link>,
          },
        ];
      case 'PROFESOR':
        return [
          {
            key: '/agenda-calendario',
            icon: <CalendarOutlined />,
            label: <Link to="/agenda-calendario">Agenda</Link>,
          },
        ];
      default:
        return [
          {
            key: '/home',
            icon: <HomeOutlined />,
            label: <Link to="/home">Inicio</Link>,
          },
        ];
    }
  };

  const menuItems = getMenuItems();

  // Determinar la clave seleccionada basada en la ruta actual
  const getSelectedKeys = () => {
    const path = location.pathname;
    
    // Buscar en items principales y en children
    for (const item of menuItems) {
      if (item.key === path) return [item.key];
      
      // Buscar en children si existen
      if (item.children) {
        const childMatch = item.children.find(child => child.key === path);
        if (childMatch) return [childMatch.key];
      }
    }
    
    return [];
  };

  // Determinar qué submenús deben estar abiertos
  const getOpenKeys = () => {
    const path = location.pathname;
    const openKeys = [];
    
    for (const item of menuItems) {
      if (item.children) {
        const childMatch = item.children.find(child => child.key === path);
        if (childMatch) {
          openKeys.push(item.key);
        }
      }
    }
    
    return openKeys;
  };

  const selectedKeys = getSelectedKeys();
  const [openKeys, setOpenKeys] = useState(getOpenKeys());

  // Actualizar openKeys cuando cambia la ruta
  useEffect(() => {
    setOpenKeys(getOpenKeys());
  }, [location.pathname]);

  // Renderizar avatar del usuario
  const renderUserMenu = () => {
    const roleToCheck = (user?.tiu_descripcion || '').toUpperCase();
    const userMenuItems = roleToCheck === 'ALUMNO'
      ? [
          {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Mi Perfil',
          },
          {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Cerrar sesión',
            danger: true,
          },
        ]
      : [
          {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Cerrar sesión',
            danger: true,
          },
        ];

    return (
      <Dropdown
        menu={{
          items: userMenuItems,
          onClick: ({ key }) => {
            if (key === 'logout') {
              handleLogout();
            } else if (key === 'profile' && roleToCheck === 'ALUMNO') {
              navigate('/perfil');
            }
          },
        }}
        placement="bottomRight"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <Avatar icon={<UserOutlined />} />
          <Text style={{ color: '#fff', display: isMobile ? 'none' : 'block' }}>
            {user?.usu_usuario || 'Usuario'}
          </Text>
        </div>
      </Dropdown>
    );
  };

  // Layout para móvil
  if (isMobile) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Header
          style={{
            background: '#2d2d2d',
            padding: '0 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            height: 64,
            borderBottom: '1px solid #404040',
          }}
        >
          <Button
            type="text"
            icon={<MenuOutlined style={{ color: '#fff', fontSize: 20 }} />}
            onClick={() => setMobileOpen(true)}
            style={{ color: '#fff' }}
          />
          <Text style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
            WinPadel
          </Text>
          <div style={{ width: 40 }} /> {/* Espaciador */}
        </Header>

        <Drawer
          title="Menú"
          placement="left"
          onClose={() => setMobileOpen(false)}
          open={mobileOpen}
          bodyStyle={{ padding: 0 }}
        >
          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={(keys) => setOpenKeys(keys)}
            items={menuItems}
            onClick={() => setMobileOpen(false)}
            style={{ borderRight: 0 }}
          />
          <div style={{ padding: 16, borderTop: '1px solid #404040', marginTop: 'auto' }}>
            <Button
              type="text"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              block
            >
              Cerrar sesión
            </Button>
          </div>
        </Drawer>

        <Content
          style={{
            marginTop: 64,
            padding: 16,
            background: '#1a1a1a',
            minHeight: 'calc(100vh - 64px)',
            color: '#e0e0e0',
          }}
        >
          {children}
        </Content>
      </Layout>
    );
  }

  // Layout para desktop
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          background: '#2d2d2d',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          height: 64,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Menu
            theme="dark"
            mode="horizontal"
            selectedKeys={selectedKeys}
            items={menuItems}
            style={{
              flex: 1,
              borderBottom: 'none',
              background: 'transparent',
              lineHeight: '64px',
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
            }}
            overflowedIndicator={null}
            disabledOverflow={false}
          />
        </div>
        <div style={{ marginLeft: '16px', flexShrink: 0 }}>
          {renderUserMenu()}
        </div>
      </Header>

      <Content
        style={{
          padding: 5,
          background: '#1a1a1a',
          minHeight: 'calc(100vh - 64px)',
          color: '#e0e0e0',
        }}
      >
        {children}
      </Content>
    </Layout>
  );
};

export default MainLayout;

